import React, { useMemo, useState } from "react";
import {
    Card,
    Table,
    Tag,
    Button,
    Input,
    Select,
    Modal,
    Spin,
    Empty,
    Space,
    Image,
} from "antd";
import dayjs from "dayjs";
import { useQuery } from "react-query";
import { secureBotAxios } from "../_helpers/auth";

const { Search } = Input;

export default function SchedulePost() {
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState(null);
    const [platformFilter, setPlatformFilter] = useState(null);
    const [preview, setPreview] = useState(null);

    //fetch - simple GET request
    const { data, isLoading, refetch } = useQuery(
        ["scheduled-posts"],
        async () => {
            try {
                const res = await secureBotAxios.get(
                    "/render/show_pending_posts"
                );

                return res?.data;
            } catch (error) {
                throw error;
            }
        },
        {
            staleTime: 30_000,
            retry: false,
        }
    );

    //normalize
    const list = useMemo(() => {
        if (!data?.data || !Array.isArray(data.data)) {
            return [];
        }
        const normalized = data.data.map((item, index) => {
            const result = {
                entry_id:
                    item.entry_id || item.job_id || Math.random().toString(),
                platform: item.platform,
                scheduled_time: item.schedule_time,
                status: item.status,
                post_status: item.post_status,
                content: item.preview,
                posting_time: item.posting_time,
                posting_account: item.posting_account,
            };
            return result;
        });
        return normalized;
    }, [data]);

    //filter
    const filteredData = useMemo(() => {
        const filtered = list.filter((item, index) => {
            const matchSearch =
                !search ||
                item.entry_id?.toLowerCase().includes(search.toLowerCase()) ||
                item.platform?.toLowerCase().includes(search.toLowerCase());

            const matchPlatform =
                !platformFilter || item.platform === platformFilter;
            const matchStatus =
                !statusFilter ||
                item.post_status === statusFilter ||
                item.status === statusFilter;

            const passes = matchSearch && matchPlatform && matchStatus;
            return passes;
        });
        return filtered;
    }, [list, search, platformFilter, statusFilter]);
    const isUrl = (str) => {
        if (!str) return false;
        return str.startsWith("http://") || str.startsWith("https://");
    };

    //display table
    const columns = [
        {
            title: "Platform",
            dataIndex: "platform",
            key: "platform",
            width: 140,
            render: (p) =>
                p ? (
                    <Tag color="blue">
                        {p.replace("_post", "").toUpperCase()}
                    </Tag>
                ) : (
                    "-"
                ),
        },
        {
            title: "Scheduled Time",
            dataIndex: "scheduled_time",
            key: "scheduled_time",
            width: 220,
            render: (t) => (t ? dayjs(t).format("DD MMM YYYY, hh:mm A") : "-"),
            sorter: (a, b) =>
                dayjs(a?.scheduled_time).unix() -
                dayjs(b?.scheduled_time).unix(),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 140,
            render: (s) => {
                let color = "default";
                let label = s;

                if (s === "pending") {
                    color = "orange";
                    label = "SCHEDULED";
                }
                if (s === "posted") {
                    color = "green";
                    label = "POSTED";
                }
                if (s === "failed") {
                    color = "red";
                    label = "FAILED";
                }

                return <Tag color={color}>{label}</Tag>;
            },
        },
        {
            title: "Actions",
            key: "actions",
            width: 180,
            render: (_, record) => (
                <Space>
                    <Button size="small" onClick={() => setPreview(record)}>
                        Preview
                    </Button>
                    <Button size="small" danger disabled>
                        Cancel
                    </Button>
                </Space>
            ),
        },
    ];
    {
        /**search and filter component for future usage */
    }
    return (
        <>
            <Card
                title="Scheduled Posts"
                extra={
                    <Space>
                        <Select
                            allowClear
                            placeholder="Platform"
                            style={{ width: 160 }}
                            onChange={setPlatformFilter}
                            options={[
                                { label: "Instagram", value: "instagram_post" },
                                { label: "Facebook", value: "facebook_post" },
                                { label: "Twitter", value: "twitter_post" },
                                { label: "LinkedIn", value: "linkedin_post" },
                            ]}
                        />
                        <Select
                            allowClear
                            placeholder="Post Status"
                            style={{ width: 160 }}
                            onChange={setStatusFilter}
                            options={[
                                { label: "Approved", value: "approved" },
                                { label: "Scheduled", value: "scheduled" },
                                { label: "Pending", value: "pending" },
                            ]}
                        />
                        <Search
                            placeholder="Search entry id / platform"
                            allowClear
                            onSearch={setSearch}
                            style={{ width: 220 }}
                        />
                    </Space>
                }
            >
                {isLoading ? (
                    <Spin />
                ) : filteredData.length === 0 ? (
                    <Empty description="No scheduled posts found" />
                ) : (
                    <Table
                        rowKey={(r) => r.entry_id}
                        columns={columns}
                        dataSource={filteredData}
                        pagination={{ pageSize: 10 }}
                    />
                )}
            </Card>

            <Modal
                open={!!preview}
                title="Scheduled Post Preview"
                footer={null}
                onCancel={() => setPreview(null)}
                width={700}
            >
                {preview && (
                    <Space
                        direction="vertical"
                        style={{ width: "100%" }}
                        size="large"
                    >
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "16px",
                                padding: "16px",
                                background: "#f5f5f5",
                                borderRadius: "8px",
                            }}
                        >
                            <div>
                                <div
                                    style={{
                                        fontSize: "12px",
                                        color: "#666",
                                        marginBottom: "4px",
                                    }}
                                >
                                    Entry ID
                                </div>
                                <div style={{ fontWeight: 500 }}>
                                    {preview.entry_id || "-"}
                                </div>
                            </div>
                            <div>
                                <div
                                    style={{
                                        fontSize: "12px",
                                        color: "#666",
                                        marginBottom: "4px",
                                    }}
                                >
                                    Platform
                                </div>
                                <div>
                                    <Tag color="blue">
                                        {preview.platform
                                            ? preview.platform
                                                  .replace("_post", "")
                                                  .toUpperCase()
                                            : "-"}
                                    </Tag>
                                </div>
                            </div>
                            <div>
                                <div
                                    style={{
                                        fontSize: "12px",
                                        color: "#666",
                                        marginBottom: "4px",
                                    }}
                                >
                                    Scheduled Time
                                </div>
                                <div style={{ fontWeight: 500 }}>
                                    {dayjs(preview.scheduled_time).format(
                                        "DD MMM YYYY, hh:mm A"
                                    )}
                                </div>
                            </div>
                            {preview.posting_time && (
                                <div>
                                    <div
                                        style={{
                                            fontSize: "12px",
                                            color: "#666",
                                            marginBottom: "4px",
                                        }}
                                    >
                                        Posted At
                                    </div>
                                    <div style={{ fontWeight: 500 }}>
                                        {dayjs(preview.posting_time).format(
                                            "DD MMM YYYY, hh:mm A"
                                        )}
                                    </div>
                                </div>
                            )}
                            <div>
                                <div
                                    style={{
                                        fontSize: "12px",
                                        color: "#666",
                                        marginBottom: "4px",
                                    }}
                                >
                                    Status
                                </div>
                                <div>
                                    {preview.status === "pending" && (
                                        <Tag color="orange">SCHEDULED</Tag>
                                    )}
                                    {preview.status === "posted" && (
                                        <Tag color="green">POSTED</Tag>
                                    )}
                                    {preview.status === "failed" && (
                                        <Tag color="red">FAILED</Tag>
                                    )}
                                </div>
                            </div>
                            {preview.post_status && (
                                <div>
                                    <div
                                        style={{
                                            fontSize: "12px",
                                            color: "#666",
                                            marginBottom: "4px",
                                        }}
                                    >
                                        Post Status
                                    </div>
                                    <div
                                        style={{
                                            fontWeight: 500,
                                            textTransform: "capitalize",
                                        }}
                                    >
                                        {preview.post_status}
                                    </div>
                                </div>
                            )}
                            {preview.posting_account && (
                                <div style={{ gridColumn: "1 / -1" }}>
                                    <div
                                        style={{
                                            fontSize: "12px",
                                            color: "#666",
                                            marginBottom: "4px",
                                        }}
                                    >
                                        Posting Account
                                    </div>
                                    <div
                                        style={{
                                            fontFamily: "monospace",
                                            fontSize: "13px",
                                            background: "#fff",
                                            padding: "8px 12px",
                                            borderRadius: "4px",
                                            border: "1px solid #d9d9d9",
                                        }}
                                    >
                                        {preview.posting_account}
                                    </div>
                                </div>
                            )}
                        </div>

                        {preview.content && (
                            <div>
                                <div
                                    style={{
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        marginBottom: "12px",
                                        color: "#262626",
                                    }}
                                >
                                    Content Preview
                                </div>
                                {isUrl(preview.content) ? (
                                    <div
                                        style={{
                                            padding: "12px",
                                            border: "1px solid #d9d9d9",
                                            borderRadius: "8px",
                                            background: "#fafafa",
                                            textAlign: "center",
                                        }}
                                    >
                                        <Image
                                            src={preview.content}
                                            alt="Post preview"
                                            style={{
                                                maxWidth: "100%",
                                                maxHeight: "400px",
                                                borderRadius: "4px",
                                            }}
                                            fallback="data:image/png;base64,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"
                                        />
                                        <div
                                            style={{
                                                marginTop: "8px",
                                                fontSize: "12px",
                                                color: "#666",
                                            }}
                                        >
                                            <a
                                                href={preview.content}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Open in new tab
                                            </a>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            padding: 16,
                                            border: "1px solid #d9d9d9",
                                            borderRadius: 8,
                                            background: "#ffffff",
                                            whiteSpace: "pre-wrap",
                                            lineHeight: 1.6,
                                            fontSize: "14px",
                                        }}
                                    >
                                        {preview.content}
                                    </div>
                                )}
                            </div>
                        )}
                    </Space>
                )}
            </Modal>
        </>
    );
}
